import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, retry, throwError, timer } from 'rxjs';
import { LoggerService } from '~ngx-shared/services';

const RETRY_COUNT = 3;
const RETRY_DELAY = 2000;

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private readonly loggerService = inject(LoggerService);

  intercept(httpRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip interceptor for '/i18n' requests
    if (httpRequest.url.includes('/i18n')) {
      return next.handle(httpRequest);
    }

    return next.handle(httpRequest).pipe(
      retry({
        count: RETRY_COUNT,
        // Only transient failures are worth repeating. A 4xx is a verdict, and retrying it
        // re-sends the whole body — an upload rejected with 413 would go out four times.
        delay: (error: HttpErrorResponse) =>
          error.status === 0 || error.status >= 500 ? timer(RETRY_DELAY) : throwError(() => error)
      }),
      catchError(this.handleError.bind(this))
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMsg = this.formatErrorMessage(error);
    const stacktrace = error.error instanceof Error ? error.error.stack : error.message;
    this.loggerService.error(errorMsg, stacktrace);
    console.error(errorMsg);
    // Rethrow the response itself: callers need `status` to react to a specific failure
    // (e.g. 413 = storage quota). HttpErrorResponse carries a readable `message` too.
    return throwError(() => error);
  }

  private formatErrorMessage(error: HttpErrorResponse): string {
    const timestamp = new Date().toISOString();
    let errorMsg = `Timestamp: ${timestamp}\n`;

    if (error.status === 0) {
      errorMsg += 'Error: Der Server ist derzeit nicht erreichbar!';
    } else if (error.error instanceof ErrorEvent) {
      errorMsg += `Client-side error: ${error.error.message}`;
    } else {
      errorMsg += `Server-side error: Code ${error.status}, Message: ${error.message}`;
    }

    if (error.url) {
      errorMsg += `\nURL: ${error.url}`;
    }

    if (error.statusText) {
      errorMsg += `\nStatus Text: ${error.statusText}`;
    }

    if (error.headers) {
      errorMsg += `\nHeaders: ${JSON.stringify(error.headers.keys().map(key => ({ [key]: error.headers.get(key) })))}`;
    }

    // Add url
    errorMsg += `\nURL: ${error.url}`;

    return errorMsg;
  }
}
